import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { registerService, checkRegistrationOTPCooldown, recordRegistrationOTPSent, type SMSConfigResponse } from '../services/registerService'
import { authService } from '../services/authService' // For getTenantId() in billing callback
import { resolveReferralToken } from '../services/affiliateService'
import {
  closeFirebaseAuthBridge,
  FIREBASE_AUTH_BRIDGE_LINE_METHOD,
  primeFirebaseAuthBridgeWindow,
  redirectToFirebaseAuthBridgeSend,
  reportFirebaseSmsBilling,
  saveLineBridgeFlowContext,
  sendOTPViaFirebaseAuthBridge,
  shouldUseFirebaseAuthBridge,
  shouldUseFirebaseAuthBridgeRedirect,
} from '../lib/firebaseAuthBridge'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import { toE164Thai } from '../utils/phone'
import { getThemeAssetUrl } from '../utils/themeUtils'
import { persistRegisterCompleteFlowState } from '../lib/registerFlowState'
import { useSmartWebSocket } from '../hooks/useSmartWebSocket'
// RecaptchaComponent removed - using ChatGPT-5 global pattern like TestSMSModal

// Global window types for Firebase (like TestSMSModal)
declare global {
  interface Window {
    recaptchaVerifier?: {
      clear?: () => void;
      render?: () => Promise<number>;
      verify?: () => Promise<string>;
    } | null;
    phoneConfirmation?: {
      confirm: (verificationCode: string) => Promise<{ user: { uid: string } }>;
    } | null;
    grecaptcha?: {
      reset?: (widgetId?: number) => void;
      render?: (container: string | HTMLElement | Element, parameters: any) => number;
      execute?: (widgetId?: number) => Promise<string>;
    };
  }
}


interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface RegisterPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const AFFILIATE_REFERRAL_KEY = 'affiliate_referral_code'

type OrderedSmsProvider = {
  id: string
  type: string
  priority: number
}

type PersistedOtpCooldown = {
  phoneNumber: string
  cooldownUntil: number
}

type PersistedOtpState = {
  phoneNumber: string
  sessionId: string
  expiresAt: number
  disableResend?: boolean
  method?: string
  provider?: string
}

const toThaiPhoneDisplay = (phoneNumber?: string) => {
  if (!phoneNumber) return ''
  const cleaned = String(phoneNumber).trim()
  if (cleaned.startsWith('+66') && cleaned.length >= 12) {
    return `0${cleaned.slice(3)}`
  }
  return cleaned
}

const getOtpCooldownStorageKey = (tenantId: string) => `huay-register-otp-cooldown:${tenantId || 'unknown'}`
const getOtpStateStorageKey = (tenantId: string) => `huay-register-otp-state:${tenantId || 'unknown'}`
const LEGACY_OTP_STATE_STORAGE_KEY = 'huay-register-otp-state'

const readPersistedOtpCooldown = (tenantId: string): PersistedOtpCooldown | null => {
  try {
    const raw = localStorage.getItem(getOtpCooldownStorageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpCooldown
    if (!parsed?.cooldownUntil || parsed.cooldownUntil <= Date.now()) {
      localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const persistOtpCooldown = (tenantId: string, data: PersistedOtpCooldown) => {
  try {
    localStorage.setItem(getOtpCooldownStorageKey(tenantId), JSON.stringify(data))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpCooldown = (tenantId: string) => {
  try {
    localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
  } catch {
    // Ignore storage cleanup failures.
  }
}

const readPersistedOtpState = (tenantId: string): PersistedOtpState | null => {
  try {
    const raw = localStorage.getItem(getOtpStateStorageKey(tenantId)) || localStorage.getItem(LEGACY_OTP_STATE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpState
    if (!parsed?.phoneNumber || !parsed?.sessionId || !parsed?.expiresAt) {
      return null
    }
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(getOtpStateStorageKey(tenantId))
      localStorage.removeItem(LEGACY_OTP_STATE_STORAGE_KEY)
      return null
    }
    return {
      ...parsed,
      phoneNumber: toThaiPhoneDisplay(parsed.phoneNumber),
    }
  } catch {
    return null
  }
}

const persistOtpState = (tenantId: string, state: PersistedOtpState) => {
  try {
    localStorage.setItem(getOtpStateStorageKey(tenantId), JSON.stringify(state))
    localStorage.setItem(LEGACY_OTP_STATE_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage failures.
  }
}

const getOrderedSmsProviders = (config: SMSConfigResponse['data'] | null | undefined): OrderedSmsProvider[] => (
  (config?.providers || [])
    .filter((provider: any) => typeof provider?.type === 'string' && provider.type.trim())
    .map((provider: any) => ({
      id: typeof provider?.id === 'string' && provider.id.trim() ? provider.id.trim() : provider.type.trim(),
      type: provider.type.trim(),
      priority: Number.isFinite(provider?.priority) ? provider.priority : 999,
    }))
    .sort((a, b) => a.priority - b.priority)
)

const RegisterPage: React.FC<RegisterPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tenantId = authService.getTenantId() || ''

  // Capture ?ref= (encrypted) or ?aff= (legacy) from URL and store for complete-registration
  useEffect(() => {
    const ref = searchParams.get('ref')
    const aff = searchParams.get('aff')
    if (ref && ref.trim()) {
      resolveReferralToken(ref.trim())
        .then(({ referralCode }) => {
          sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, referralCode)
        })
        .catch(() => { /* invalid/expired ref */ })
    } else if (aff && aff.trim()) {
      sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, aff.trim())
    }
  }, [searchParams])

  // Get theme-specific assets path
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }
  
  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme
  
  // Registration form state
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false) // Flag to prevent further execution after navigation
  /** เวลาที่กดขอ OTP ได้อีก (timestamp) — หลังกดขอ OTP แล้วหรือได้ 429 ต้องรอจนถึงเวลานี้ */
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const [, setTick] = useState(0)
  
  // Real-time phone validation state
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [phoneExistsInSystem, setPhoneExistsInSystem] = useState(false)
  const [phoneValidationMessage, setPhoneValidationMessage] = useState('')
  
  // SMS configuration state
  const [smsConfig, setSmsConfig] = useState<SMSConfigResponse['data'] | null>(null)
  const [smsConfigLoading, setSmsConfigLoading] = useState(true)
  const smsConfigLoadedRef = useRef(false) // Prevent duplicate API calls
  const isRecaptchaInitializingRef = useRef(false) // Prevent duplicate reCAPTCHA initialization
  
  // reCAPTCHA state with ChatGPT-5 Global Pattern (like TestSMSModal)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null)
  const [recaptchaMode, setRecaptchaMode] = useState<'none' | 'immediate' | 'background'>('none')
  const [hasRecoverableOtpState, setHasRecoverableOtpState] = useState(false)
  
  // Ref สำหรับให้ onContentUpdate เรียก reloadSMSConfig ล่าสุด (reloadSMSConfig ถูก define ด้านล่าง)
  const reloadSMSConfigRef = useRef<() => Promise<void>>(() => Promise.resolve())

  useEffect(() => {
    if (!tenantId) return
    const persisted = readPersistedOtpCooldown(tenantId)
    if (!persisted) return

    setCooldownUntil(persisted.cooldownUntil)
    setPhoneNumber((prev) => prev || persisted.phoneNumber || '')
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) return
    const persisted = readPersistedOtpState(tenantId)
    setHasRecoverableOtpState(!!persisted)
    if (persisted?.phoneNumber) {
      setPhoneNumber((prev) => prev || persisted.phoneNumber || '')
    }
  }, [tenantId, cooldownUntil])

  // เมื่อมีเบอร์ครบ 10 หลัก — เช็ค cooldown จาก backend (ดักที่ backend ไม่ให้ reload ลัด)
  useEffect(() => {
    if (!phoneNumber || phoneNumber.length !== 10) return
    if (!/^0[689]\d{8}$/.test(phoneNumber)) return
    checkRegistrationOTPCooldown(phoneNumber).then((r) => {
      if (!r.allowed && (r.remainingTimeMs ?? 0) > 0) {
        setCooldownUntil(Date.now() + (r.remainingTimeMs ?? 0))
      }
    })
  }, [phoneNumber])

  useEffect(() => {
    if (!tenantId) return

    if (cooldownUntil != null && cooldownUntil > Date.now()) {
      const existing = readPersistedOtpCooldown(tenantId)
      persistOtpCooldown(tenantId, {
        phoneNumber: phoneNumber || existing?.phoneNumber || '',
        cooldownUntil,
      })
      return
    }

    clearPersistedOtpCooldown(tenantId)
  }, [tenantId, phoneNumber, cooldownUntil])

  const navigateToPersistedOtpPage = () => {
    if (!tenantId) return
    const persisted = readPersistedOtpState(tenantId)
    if (!persisted) {
      setHasRecoverableOtpState(false)
      return
    }

    navigate('/register/otp', {
      state: persisted,
      replace: true,
    })
  }

  // อัปเดต countdown ทุก 1 วินาที เมื่ออยู่ระหว่าง cooldown
  useEffect(() => {
    if (cooldownUntil == null) return
    const remaining = cooldownUntil - Date.now()
    if (remaining <= 0) {
      setCooldownUntil(null)
      return
    }
    const t = setInterval(() => {
      setTick((n) => n + 1)
      if (Date.now() >= cooldownUntil) setCooldownUntil(null)
    }, 1000)
    return () => clearInterval(t)
  }, [cooldownUntil])

  // 📡 Real-time SMS Configuration Updates via WebSocket
  const handleConfigUpdate = (configType: string, _configData: any, metadata?: any) => {
    console.log('⚙️ Real-time configuration update received:', {
      configType,
      metadata,
      currentConfig: smsConfig ? 'loaded' : 'not loaded'
    })

    // 🧪 DEBUG: Log config update details (removed toast for cleaner UX)
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 handleConfigUpdate Details:', {
        configType,
        updateType: metadata?.updateType,
        currentSMSConfig: smsConfig ? 'loaded' : 'not loaded',
        willReload: configType === 'sms_provider' || metadata?.updateType === 'tenant_provider_preferences_change'
      });
    }

    if (configType === 'sms_provider' || metadata?.updateType === 'tenant_provider_preferences_change') {
      // SMS provider configuration changed - need to reload SMS config
      console.log('📱 SMS provider configuration changed, reloading SMS config...', {
        updateType: metadata?.updateType,
        isGlobal: metadata?.updateType === 'provider_configuration_change',
        isTenantSpecific: metadata?.updateType === 'tenant_provider_preferences_change'
      })
      
      // Show notification to user
      const notificationTitle = metadata?.updateType === 'tenant_provider_preferences_change' 
        ? 'การตั้งค่า SMS ของคุณมีการเปลี่ยนแปลง'
        : 'การตั้งค่า SMS มีการเปลี่ยนแปลง';
      
      const notificationText = metadata?.updateType === 'tenant_provider_preferences_change'
        ? 'Admin ได้เปลี่ยนการตั้งค่า SMS provider ของคุณ'
        : 'ระบบจะอัปเดตการตั้งค่าใหม่โดยอัตโนมัติ';

      Swal.fire({
        title: notificationTitle,
        text: notificationText,
        icon: 'info',
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })

      reloadSMSConfigRef.current()
    }
  }

  // เมื่อ TenantContent เปลี่ยน (รวมเปิด/ปิด OTP จากตั้งค่าหน้าบ้าน) — อัปเดต SMS config ทันทีแบบ real-time (เหมือน user-modern)
  const handleContentUpdate = useCallback((content: any) => {
    if (content && typeof content === 'object' && content.features && 'smsOtpEnabled' in content.features) {
      console.log('📱 Tenant content features changed (smsOtpEnabled), reloading SMS config...')
      reloadSMSConfigRef.current()
    }
  }, [])

  // 🔌 WebSocket connection for real-time updates
  useSmartWebSocket({
    tenantId: authService.getTenantId(),
    onContentUpdate: handleContentUpdate,
    onConfigUpdate: handleConfigUpdate,
    enabled: true
  })
  
  // 🎯 Smart reCAPTCHA Mode Decision Logic
  const determineRecaptchaMode = (config: any): 'none' | 'immediate' | 'background' => {
    if (shouldUseFirebaseAuthBridge()) {
      console.log('🪟 Firebase bridge enabled - skipping tenant-domain reCAPTCHA initialization')
      return 'none'
    }

    console.log('🔍 Determining reCAPTCHA mode with config:', {
      smsEnabled: config?.smsEnabled,
      providers: config?.providers,
      fallbackEnabled: config?.fallbackEnabled
    })
    
    if (!config || !config.smsEnabled) {
      return 'none'
    }
    // เมื่อเปิด SMS แต่ไม่มีรายการ provider (config โหลดไม่ครบ) ให้ถือว่าเป็น Firebase และเปิด reCAPTCHA
    if (!config.providers || config.providers.length === 0) {
      console.log('🔄 No providers list — assuming Firebase (recaptchaMode: background)')
      return 'background'
    }
    
        // Sort providers by priority and filter enabled ones
        console.log('🔍 All providers before filtering:', config.providers.map((p: any) => `${p.type}(priority:${p.priority},enabled:${p.enabled})`))
        const enabledProviders = config.providers
          .filter((p: any) => p.enabled !== false) // Accept undefined as enabled
          .sort((a: any, b: any) => a.priority - b.priority)
        
        console.log('🔍 Enabled providers after filtering:', enabledProviders.map((p: any) => `${p.type}(priority:${p.priority},enabled:${p.enabled})`))
    
    if (enabledProviders.length === 0) {
      console.log('❌ No enabled providers found!')
      return 'none'
    }
    
    const hasFirebase = enabledProviders.some((p: any) => p.type === 'firebase')
    const primaryProvider = enabledProviders[0]?.type // Lowest priority number = highest priority
    
    console.log('🎯 Provider Analysis:', {
      primaryProvider,
      hasFirebase,
      fallbackEnabled: config.fallbackEnabled,
      enabledProviders: enabledProviders.map((p: any) => `${p.type}(${p.priority})`)
    })
    
    if (primaryProvider === 'firebase') {
      // Firebase is primary → initialize reCAPTCHA in background (invisible + ready)
      console.log('🛡️ reCAPTCHA Mode: BACKGROUND (Firebase Primary - Invisible)')
      return 'background'
    } else if (hasFirebase && config.fallbackEnabled) {
      // Firebase is fallback → initialize reCAPTCHA in background (invisible + ready)
      console.log('🛡️ reCAPTCHA Mode: BACKGROUND (Firebase Fallback)')
      return 'background'
    } else {
      // No Firebase at all → no reCAPTCHA needed
      console.log('📞 reCAPTCHA Mode: NONE (No Firebase)')
      return 'none'
    }
  }

  // Initialize reCAPTCHA using ChatGPT-5 Global Pattern (like TestSMSModal)
  const initializeRecaptcha = async (retryCount = 0) => {
    const maxRetries = 3
    
    try {
      console.log(`📱 Initializing reCAPTCHA (ChatGPT-5 global pattern) - attempt ${retryCount + 1}...`)
      setRecaptchaError(null)
      
      // 🛑 CRITICAL: Check if initialization is in progress to prevent duplicates
      if (isRecaptchaInitializingRef.current) {
        console.log('⏭️ reCAPTCHA initialization already in progress, skipping duplicate')
        return
      }
      
      // Check if reCAPTCHA is already ready to avoid duplicate initialization
      if (recaptchaReady && window.recaptchaVerifier && retryCount === 0) {
        console.log('⏭️ reCAPTCHA already initialized and ready, skipping')
        return
      }
      
      // Set flag to prevent concurrent initialization
      isRecaptchaInitializingRef.current = true
      
      // Clear any existing verifier first (Critical for Enterprise/v2 mismatch fix)
      window.recaptchaVerifier?.clear?.()
      window.recaptchaVerifier = null
      
      // Import Firebase functions (ChatGPT-5 pattern)
      const { getFirebaseAuth } = await import('../lib/firebase')
      const firebaseAuth = await import('firebase/auth')
      const { RecaptchaVerifier } = firebaseAuth
      
      const auth = await getFirebaseAuth()
      if (!auth) {
        throw new Error('Firebase not available')
      }
      
      // ✅ Wait for DOM element with retry logic (Critical for timing issues)
      let containerElement = document.getElementById('register-recaptcha-container')
      
      // If element not found, wait a bit and retry
      if (!containerElement && retryCount < maxRetries) {
        console.log(`⏳ reCAPTCHA container not ready, waiting... (attempt ${retryCount + 1}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, 500))
        containerElement = document.getElementById('register-recaptcha-container')
      }
      
      if (!containerElement) {
        if (retryCount < maxRetries) {
          console.log(`🔄 Retrying reCAPTCHA initialization (${retryCount + 1}/${maxRetries})...`)
          return initializeRecaptcha(retryCount + 1)
        }
        throw new Error('reCAPTCHA container element not found in DOM after retries. Please ensure the container is rendered.')
      }
      
      // Wait for layout so reCAPTCHA can attach (match TestSMSModal - avoids auth/argument-error)
      await new Promise((r) => requestAnimationFrame(r))
      
      // Create NEW v2 invisible verifier (match TestSMSModal: use string ID, not DOM element)
      console.log('📱 Creating fresh invisible reCAPTCHA v2 verifier for registration...')
      console.log('🔧 reCAPTCHA configuration:', {
        size: 'invisible',
        authDomain: auth.config.authDomain,
        projectId: auth.config.projectId,
        containerId: 'register-recaptcha-container'
      })
      
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'register-recaptcha-container',
        {
          size: 'invisible',
          callback: () => {
            console.log('✅ Registration reCAPTCHA v2 verified automatically')
            setRecaptchaReady(true)
            setRecaptchaError(null)
          },
          'expired-callback': () => {
            console.warn('⏰ Registration reCAPTCHA v2 expired - clearing verifier')
            window.recaptchaVerifier?.clear?.()
            window.recaptchaVerifier = null
            setRecaptchaReady(false)
          },
          'error-callback': (error: any) => {
            console.error('❌ reCAPTCHA error callback:', error)
            setRecaptchaError(error.message || 'reCAPTCHA error')
          }
        }
      )
      
      // Render the reCAPTCHA to make it ready
      if (window.recaptchaVerifier?.render) {
        try {
          await window.recaptchaVerifier.render()
          console.log('✅ Registration reCAPTCHA rendered and ready')
          
          // Update UI state - CRITICAL for UI display
          setRecaptchaReady(true)
          setRecaptchaError(null)
        } catch (renderError: any) {
          if (renderError.message?.includes('already been rendered')) {
            console.log('⚠️ reCAPTCHA already rendered, using existing instance')
            setRecaptchaReady(true)
            setRecaptchaError(null)
          } else {
            console.error('❌ reCAPTCHA render error:', renderError)
            console.error('❌ Error details:', {
              message: renderError.message,
              code: renderError.code,
              stack: renderError.stack
            })
            throw renderError
          }
        }
      }
      
    } catch (error: unknown) {
      console.error('❌ Registration reCAPTCHA initialization failed:', error)
      
      // Clear verifier on error
      window.recaptchaVerifier?.clear?.()
      window.recaptchaVerifier = null
      setRecaptchaReady(false)
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setRecaptchaError(errorMessage)
    } finally {
      // Always reset the initialization flag
      isRecaptchaInitializingRef.current = false
    }
  }

  // Load SMS configuration on component mount
  useEffect(() => {
    loadSMSConfig()
  }, [])
  
  // Initialize reCAPTCHA after DOM is ready (ChatGPT-5 pattern)
  useEffect(() => {
    // Only initialize when:
    // 1. reCAPTCHA is needed (background mode only)
    // 2. SMS config is loaded (!smsConfigLoading)
    // 3. Not already ready
    // 4. Not already initializing
    if (
      recaptchaMode === 'background' && 
      !smsConfigLoading && 
      !recaptchaReady &&
      !isRecaptchaInitializingRef.current
    ) {
      console.log(`🔐 DOM ready! Initializing reCAPTCHA in ${recaptchaMode} mode...`)
      
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        initializeRecaptcha()
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [recaptchaMode, smsConfigLoading, recaptchaReady])

  // Cleanup on unmount (ChatGPT-5 pattern)
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up registration reCAPTCHA verifier...')
      window.recaptchaVerifier?.clear?.()
      window.recaptchaVerifier = null
    }
  }, [])

        // 🔄 Reload SMS Configuration (for real-time updates)
        const reloadSMSConfig = async () => {
    try {
                console.log('🔄 Reloading SMS config due to configuration change...')
      setSmsConfigLoading(true)
                
                const response = await registerService.getSMSConfig()
                console.log('📱 SMS config reloaded from Configuration Service:', {
                    success: response.success,
                    smsEnabled: response.data?.smsEnabled,
                    fallbackEnabled: response.data?.fallbackEnabled,
                    recaptchaMode: response.data ? determineRecaptchaMode(response.data) : 'none',
                    providers: response.data?.providers?.map(p => `${p.type}(priority:${p.priority})`)?.join(', ') || 'none'
                })

                if (response.success && response.data) {
                    setSmsConfig(response.data)
                    
                    // Update reCAPTCHA mode based on new configuration
                    const newRecaptchaMode = determineRecaptchaMode(response.data)
                    const oldRecaptchaMode = recaptchaMode
                    
                    if (newRecaptchaMode !== oldRecaptchaMode) {
                        console.log(`🔄 reCAPTCHA mode changed: ${oldRecaptchaMode} → ${newRecaptchaMode}`)
                        
                        // 🛑 CRITICAL: Set a flag to prevent duplicate initialization during cleanup
                        isRecaptchaInitializingRef.current = true
                        
                        setRecaptchaMode(newRecaptchaMode)
                        
                        // Clear existing reCAPTCHA completely
                        if (window.recaptchaVerifier) {
                            console.log('🧹 Clearing existing reCAPTCHA due to config change')
                            try {
                                window.recaptchaVerifier.clear?.()
                            } catch (clearError) {
                                console.warn('⚠️ Error clearing reCAPTCHA:', clearError)
                            }
                            window.recaptchaVerifier = null
                            setRecaptchaReady(false)
                            setRecaptchaError(null)
                        }
                        
                        // Clear the reCAPTCHA container DOM
                        const recaptchaContainer = document.getElementById('register-recaptcha-container')
                        if (recaptchaContainer) {
                            recaptchaContainer.innerHTML = ''
                            console.log('🧹 Cleared reCAPTCHA container DOM')
                        }
                        
                        // Clear global reCAPTCHA widgets
                        if (window.grecaptcha && window.grecaptcha.reset) {
                            try {
                                window.grecaptcha.reset()
                                console.log('🧹 Reset global reCAPTCHA widgets')
                            } catch (resetError) {
                                console.warn('⚠️ Error resetting global reCAPTCHA:', resetError)
                            }
                        }
                        
                        // Force UI re-render by updating state
                        setSmsConfigLoading(true)
                        setTimeout(() => {
                            setSmsConfigLoading(false)
                        }, 100)
                        
                        // Initialize new reCAPTCHA if needed (with longer delay and flag protection)
                        if (newRecaptchaMode === 'immediate') {
                            setTimeout(() => {
                                isRecaptchaInitializingRef.current = false // Allow initialization now
                                console.log('🔄 Initializing reCAPTCHA after config change cleanup...')
                                initializeRecaptcha()
                            }, 2000) // Increased delay for complete cleanup
                        } else {
                            // Reset flag if not initializing
                            setTimeout(() => {
                                isRecaptchaInitializingRef.current = false
                            }, 500)
                        }
                    }
                }
            } catch (error) {
                console.error('❌ Error reloading SMS config:', error)
                isRecaptchaInitializingRef.current = false // Reset flag on error
                Swal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถอัปเดตการตั้งค่า SMS ได้ กรุณาลองใหม่อีกครั้ง',
                    icon: 'error',
                    confirmButtonText: 'ตกลง'
                })
            } finally {
                setSmsConfigLoading(false)
            }
        }

  reloadSMSConfigRef.current = reloadSMSConfig

  const loadSMSConfig = async () => {
    // Prevent duplicate calls during React StrictMode
    if (smsConfigLoadedRef.current) {
      console.log('⏭️ SMS config already loaded, skipping duplicate call')
      return
    }
    
    smsConfigLoadedRef.current = true
    
    try {
      console.log('🔄 Loading SMS config for registration...')
      const response = await registerService.getSMSConfig()
      
      if (response.success && response.data) {
        setSmsConfig(response.data)
        
        // 🎯 Smart reCAPTCHA Initialization Logic
        const recaptchaMode = determineRecaptchaMode(response.data)
        setRecaptchaMode(recaptchaMode)
        
        console.log('📱 SMS config loaded with Smart reCAPTCHA:', {
          smsEnabled: response.data.smsEnabled,
          fallbackEnabled: response.data.fallbackEnabled,
          recaptchaMode: recaptchaMode,
          providers: response.data.providers?.map((p: any) => `${p.type}(priority:${p.priority})`).join(', ')
        })
        
        // Don't initialize reCAPTCHA here - DOM not ready yet
        // Will be initialized in separate useEffect after DOM renders
      } else {
        console.warn('⚠️ Failed to load SMS config:', response.error)
        setRecaptchaMode('none')
      }
    } catch (error) {
      console.error('❌ Error loading SMS config:', error)
      smsConfigLoadedRef.current = false // Reset on error to allow retry
    } finally {
      setSmsConfigLoading(false)
    }
  }

  // 🔍 Real-time phone availability checker
  const checkPhoneAvailability = async (phone: string) => {
    try {
      // Validate phone format first (but use Thai format for check-mobile API)
      toE164Thai(phone) // Just validate, don't use the result
      
      setIsCheckingPhone(true)
      setPhoneValidationMessage('กำลังตรวจสอบเบอร์มือถือ...')
      
      console.log('🔍 Real-time checking phone availability for:', phone)
      // Use Thai format (0616254799) for check-mobile API
      const thaiFormatPhone = phone.startsWith('0') ? phone : `0${phone}`
      const response = await registerService.checkMobileAvailability(thaiFormatPhone)
      
      if (response.success) {
        if (response.available) {
          // Phone is available for registration
          setPhoneExistsInSystem(false)
          setPhoneValidationMessage('✅ เบอร์มือถือพร้อมใช้งาน')
        } else {
          // Phone exists in system - show popup and redirect
          setPhoneExistsInSystem(true)
          setPhoneValidationMessage('❌ เบอร์มือถือนี้มีในระบบแล้ว')
          
          // Show countdown popup and redirect to login
          await showPhoneExistsPopup(phone)
        }
      } else {
        // API error
        setPhoneExistsInSystem(false)
        setPhoneValidationMessage('⚠️ ไม่สามารถตรวจสอบเบอร์มือถือได้')
        console.error('❌ Phone availability check failed:', response.error)
      }
    } catch (error) {
      console.error('❌ Error checking phone availability:', error)
      setPhoneExistsInSystem(false)
      
      // Show specific error message for phone format issues
      if (error instanceof Error && error.message.includes('รูปแบบ')) {
        setPhoneValidationMessage(`❌ ${error.message}`)
      } else {
      setPhoneValidationMessage('⚠️ เกิดข้อผิดพลาดในการตรวจสอบ')
      }
    } finally {
      setIsCheckingPhone(false)
    }
  }

  // 🚨 เบอร์มีในระบบแล้ว: แจ้งเตือน + นับถอยหลัง 15 วินาที (ห้ามปิด) แล้ว redirect ไป /login
  const showPhoneExistsPopup = async (phone: string) => {
    await Swal.fire({
      icon: 'warning',
      title: 'มีเบอร์นี้ในระบบแล้ว',
      html: `
        <p>เบอร์ <strong>${phone}</strong> มีในระบบอยู่แล้ว</p>
        <p class="mb-0">หากไม่ใช่ข้อมูลคุณ กรุณาติดต่อแอดมิน</p>
        <p class="mt-2 mb-0">จะนำคุณไปยังหน้าเข้าสู่ระบบใน <span id="countdown">15</span> วินาที</p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      timer: 15000,
      timerProgressBar: true,
      didOpen: () => {
        const countdownElement = document.getElementById('countdown')
        let countdown = 15
        const interval = setInterval(() => {
          countdown--
          if (countdownElement) {
            countdownElement.textContent = countdown.toString()
          }
          if (countdown <= 0) {
            clearInterval(interval)
          }
        }, 1000)
      }
    })
    navigate('/login', { replace: true })
  }

  // reCAPTCHA handlers removed - using ChatGPT-5 global pattern

  // Phone validation removed - using E.164 validation directly in functions
  
  // Validation functions
  const validateForm = (): string | null => {
    if (!phoneNumber.trim()) {
      return 'กรุณากรอกหมายเลขโทรศัพท์มือถือ'
    }
    
    try {
      toE164Thai(phoneNumber) // Validate format
    } catch (error) {
      if (error instanceof Error) {
        return error.message
      }
      return 'กรุณากรอกหมายเลขโทรศัพท์มือถือที่ถูกต้อง'
    }

    // Check if phone is being validated
    if (isCheckingPhone) {
      return 'กำลังตรวจสอบเบอร์มือถือ กรุณารอสักครู่...'
    }

    // Check if phone exists in system
    if (phoneExistsInSystem) {
      return 'เบอร์มือถือนี้มีในระบบแล้ว กรุณาใช้เบอร์อื่น หรือเข้าสู่ระบบ'
    }

    // Check SMS configuration (allow both enabled and disabled)
    if (!smsConfig) {
      return 'ไม่สามารถโหลดการตั้งค่าระบบได้ กรุณาลองใหม่อีกครั้ง'
    }

    // For background mode, don't block if reCAPTCHA is not ready yet
    // Backend will handle fallback appropriately
    // reCAPTCHA will be handled automatically in background

    return null
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Check if navigation is already in progress
    if (isNavigating) {
      console.log('🔄 Navigation already in progress, preventing duplicate submission')
      return
    }
    
    setIsSubmitting(true)
    
    // Validate form first
    const validationError = validateForm()
    console.log('🔍 Form validation result:', {
      validationError,
      phoneNumber,
      isCheckingPhone,
      phoneExistsInSystem,
      smsConfig: !!smsConfig,
      recaptchaMode,
      recaptchaReady,
      recaptchaError
    })
    
    if (validationError) {
      console.log('❌ Form validation failed:', validationError)
      setIsSubmitting(false) // Reset submitting state on validation error
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: validationError,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }
    
    console.log('✅ Form validation passed, proceeding with SMS...')
    
    try {
      const enabledProviders = getOrderedSmsProviders(smsConfig)
      const primaryProvider = enabledProviders[0]?.type || 'unknown'
      const isFirebaseProvider = primaryProvider === 'firebase'
      const tryFirebaseClient = !!smsConfig?.smsEnabled && (isFirebaseProvider || primaryProvider === 'unknown' || enabledProviders.length === 0)
      const bridgeEnabled = tryFirebaseClient && shouldUseFirebaseAuthBridge()
      const lineBridgeRedirect = bridgeEnabled && shouldUseFirebaseAuthBridgeRedirect()
      let bridgePrimed = false

      // Check if navigation is in progress (user is being redirected due to rate limit)
      if (isNavigating) {
        console.log('🔄 Navigation in progress, stopping form submission')
        return
      }

      if (bridgeEnabled && !lineBridgeRedirect) {
        try {
          primeFirebaseAuthBridgeWindow()
          bridgePrimed = true
        } catch (primeError) {
          console.warn('⚠️ Failed to pre-open Firebase auth bridge popup:', primeError)
        }
      }

      // 🔍 เช็คเบอร์มีในระบบหรือไม่ ตอนกด OTP/ถัดไป (ไม่พึ่งแค่ real-time จากการพิมพ์)
      const thaiPhone = phoneNumber.trim().startsWith('0') ? phoneNumber.trim() : `0${phoneNumber.trim()}`
      const checkResponse = await registerService.checkMobileAvailability(thaiPhone)
      if (checkResponse.success && !checkResponse.available) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        setIsSubmitting(false)
        await showPhoneExistsPopup(thaiPhone)
        return
      }

      // ดักที่ backend: เช็ค cooldown ก่อนขอ OTP (ไม่ส่งไป provider ถ้ายังอยู่ในช่วงรอ)
      const cooldownCheck = await checkRegistrationOTPCooldown(thaiPhone)
      if (!cooldownCheck.allowed && (cooldownCheck.remainingTimeMs ?? 0) > 0) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        setCooldownUntil(Date.now() + (cooldownCheck.remainingTimeMs ?? 0))
        await Swal.fire({
          title: 'กรุณารออีกก่อนขอ OTP ใหม่',
          text: `กรุณารออีก ${cooldownCheck.remainingTimeMin ?? 0} นาที จึงสามารถขอ OTP ใหม่ได้`,
          icon: 'warning',
          confirmButtonText: 'ตกลง'
        })
        setIsSubmitting(false)
        return
      }
      
      console.log('📱 Phone validation passed, checking SMS configuration for:', phoneNumber)
      
      // 🎯 Conditional Flow: SMS Enabled vs Disabled
      if (!smsConfig?.smsEnabled) {
        console.log('📝 SMS disabled for this tenant - skipping OTP verification')
        
        // Set navigation flag to prevent further execution
        setIsNavigating(true)
        
        await Swal.fire({
          icon: 'info',
          title: 'ข้อมูลถูกต้อง',
          text: 'SMS verification ถูกปิดการใช้งาน จะข้ามไปขั้นตอนการกรอกข้อมูลส่วนตัว',
          confirmButtonText: 'ดำเนินการต่อ',
          confirmButtonColor: '#28a745',
          timer: 2000,
          timerProgressBar: true
        })
        
        // Navigate directly to complete registration (skip OTP)
        const completeState = {
          phoneNumber,
          sessionId: `no_sms_${Date.now()}`,
          smsVerified: true as const,
          method: 'no-sms',
          provider: 'none',
        }
        persistRegisterCompleteFlowState(completeState)
        navigate('/register/complete', {
          state: completeState,
          replace: true,
        })
        return
      }
      
      // Phone availability already checked in onChange - proceed with SMS verification
      console.log('📱 SMS enabled - sending SMS verification to:', phoneNumber)
      
      // Convert to E.164 format for SMS service
      const e164Phone = toE164Thai(phoneNumber)
      
      console.log('🎯 Provider selection for SMS:', {
        enabledProviders: enabledProviders.map((p: any) => `${p.type}(priority:${p.priority})`),
        primaryProvider,
        isFirebaseProvider,
        tryFirebaseClient
      })
      
      // 🛡️ Check SMS rate limit before sending
      try {
        console.log('🛡️ Checking SMS rate limit for:', e164Phone.replace(/(\+\d{2})\d+(\d{4})/, '$1****$2'))
        const apiBase = getApiBaseUrl()
        const rateLimitResponse = await fetch(`${apiBase}/api/v1/user-auth/sms-rate-limit-status?phoneNumber=${encodeURIComponent(e164Phone)}&messageType=registration`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': authService.getTenantId() || ''
          },
          credentials: 'include'
        })
        
        if (rateLimitResponse.ok) {
          const rateLimitData = await rateLimitResponse.json()
          console.log('🛡️ Rate limit check result:', rateLimitData)
          
          if (!rateLimitData.data.allowed) {
            const { reason, remainingTime, rateLimitType } = rateLimitData.data.rateLimitCheck
            
            if (rateLimitType === 'cooldown' && remainingTime) {
              // User recently sent SMS - skip sending new SMS but allow OTP verification
              const minutes = Math.ceil(remainingTime / 60)
              
              console.log('🛡️ SMS cooldown active, proceeding to OTP verification without sending new SMS', {
                remainingTime,
                minutes
              })
              
              // Auto-redirect with countdown
              let countdown = 5
              Swal.fire({
                title: 'OTP ยังใช้งานได้',
                html: `คุณได้รับ OTP แล้ว ${minutes} นาทีที่แล้ว<br><br>กรุณากรอก OTP ที่ได้รับ หรือรอ ${minutes} นาทีสำหรับ OTP ใหม่<br><br><strong>จะเปลี่ยนหน้าอัตโนมัติใน <span id="countdown">${countdown}</span> วินาที</strong>`,
                icon: 'info',
                timer: 5000,
                timerProgressBar: true,
                showConfirmButton: true,
                confirmButtonText: 'ไปยืนยัน OTP ทันที',
                allowOutsideClick: false,
                didOpen: () => {
                  const countdownElement = document.getElementById('countdown')
                  const interval = setInterval(() => {
                    countdown--
                    if (countdownElement) {
                      countdownElement.textContent = countdown.toString()
                    }
                    if (countdown <= 0) {
                      clearInterval(interval)
                    }
                  }, 1000)
                }
              }).then(() => {
                if (bridgePrimed) closeFirebaseAuthBridge()
                // Set navigation flag to prevent further execution
                setIsNavigating(true)
                const otpState = {
                  phoneNumber,
                  sessionId: `cooldown_${Date.now()}`,
                  expiresAt: Date.now() + (remainingTime * 1000),
                  method: 'existing-otp',
                  provider: primaryProvider || 'unknown',
                  disableResend: true,
                }
                persistOtpState(authService.getTenantId() || '', otpState)
                
                // Navigate to OTP page regardless of how the dialog was closed
                navigate('/register/otp', {
                  state: otpState,
                  replace: true
                })
              })
              
              // Don't reset isSubmitting here - let the navigation handle it
            return
          } else {
              // Other rate limits (hourly/daily) - completely block
              let errorMessage = reason
              
              if (rateLimitType === 'hourly') {
                errorMessage = 'คุณได้ขอ OTP เกินขีดจำกัดต่อชั่วโมงแล้ว\nกรุณาลองใหม่ในชั่วโมงถัดไป'
              } else if (rateLimitType === 'daily') {
                errorMessage = 'คุณได้ขอ OTP เกินขีดจำกัดต่อวันแล้ว\nกรุณาติดต่อฝ่ายสนับสนุน'
              }
              
              Swal.fire({
                title: 'ไม่สามารถส่ง SMS ได้',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'ตกลง'
              })
              if (bridgePrimed) closeFirebaseAuthBridge()
              setIsSubmitting(false)
              return
            }
          }
      } else {
          console.warn('⚠️ Rate limit check failed, proceeding with SMS send')
        }
      } catch (rateLimitError) {
        console.warn('⚠️ Rate limit check error, proceeding with SMS send:', rateLimitError)
      }
      
      // Firebase bridge-only mode for tenant domains.
      if (tryFirebaseClient) {
        if (!bridgeEnabled) {
          if (bridgePrimed) closeFirebaseAuthBridge()
          await Swal.fire({
            icon: 'error',
            title: 'OTP ยังไม่พร้อม',
            text: 'ระบบนี้ถูกตั้งค่าให้ใช้ Firebase ผ่าน central auth bridge เท่านั้น',
            confirmButtonText: 'ตกลง',
            confirmButtonColor: '#dc3545'
          })
          return
        }

        try {
          const apiBaseUrl = getApiBaseUrl()
          const tenantId = authService.getTenantId()
          if (!tenantId) {
            throw new Error('ไม่พบ tenant สำหรับส่ง OTP')
          }

          if (lineBridgeRedirect) {
            const sessionId = `firebase_bridge_line_${Date.now()}`
            const navigationState = {
              phoneNumber,
              sessionId,
              expiresAt: Date.now() + 300000,
              method: FIREBASE_AUTH_BRIDGE_LINE_METHOD,
              provider: 'firebase',
              canResendAfter: 300000,
              disableResend: true,
            }
            persistOtpState(tenantId, navigationState)

            const flowId = crypto.randomUUID()
            saveLineBridgeFlowContext({
              flowId,
              tenantId,
              phoneNumber: e164Phone,
              apiBaseUrl,
              sessionId,
              createdAt: Date.now(),
            })

            redirectToFirebaseAuthBridgeSend({
              tenantId,
              phoneNumber: e164Phone,
              apiBaseUrl,
              returnUrl: `${window.location.origin}/register/otp`,
            }, flowId)

            return
          }

          console.log('🪟 Using central Firebase auth bridge (bridge-only mode)...')
          await sendOTPViaFirebaseAuthBridge({
            tenantId,
            phoneNumber: e164Phone,
            apiBaseUrl,
          })

          recordRegistrationOTPSent(phoneNumber).catch(() => {})
          setCooldownUntil(Date.now() + 300000)
          setIsNavigating(true)

          const navigationState = {
            phoneNumber,
            sessionId: 'firebase_bridge_' + Date.now(),
            expiresAt: Date.now() + 300000,
            method: 'firebase-auth-bridge',
            provider: 'firebase',
            canResendAfter: 300000,
            disableResend: true,
          }
          persistOtpState(tenantId, navigationState)

          navigate('/register/otp', {
            state: navigationState,
            replace: true,
          })

          setTimeout(() => {
            reportFirebaseSmsBilling({
              apiBaseUrl,
              tenantId,
              phoneNumber: e164Phone,
              status: 'sent',
              messageType: 'registration',
              source: 'frontend-classic-registration-bridge',
            })
          }, 100)

          return
        } catch (bridgeError) {
          if (bridgePrimed) closeFirebaseAuthBridge()
          await Swal.fire({
            title: 'ส่ง SMS ล้มเหลว',
            text: bridgeError instanceof Error ? bridgeError.message : 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ',
            icon: 'error',
            confirmButtonText: 'ตกลง'
          })
          return
        }
      }

      if (bridgePrimed) closeFirebaseAuthBridge()
      await Swal.fire({
        icon: 'error',
        title: 'OTP ยังไม่พร้อม',
        text: 'ระบบนี้ถูกตั้งค่าให้ใช้ Firebase ผ่าน central auth bridge เท่านั้น',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
      return
      
    } catch (error) {
      console.error('❌ Registration error:', error)
      
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // Format phone number input with real-time validation
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '') // Remove non-digits
    if (value.length <= 10) {
      setPhoneNumber(value)
      
      // 🔍 Real-time phone availability check when exactly 10 digits
      if (value.length === 10) {
        try {
          // Validate that it can be converted to E.164 (for SMS later)
          toE164Thai(value)
        console.log('📱 Phone number complete, checking availability:', value)
        checkPhoneAvailability(value)
        } catch (error) {
          // Show validation error for invalid format
          setPhoneExistsInSystem(false)
          setPhoneValidationMessage('❌ รูปแบบเบอร์มือถือไม่ถูกต้อง')
          setIsCheckingPhone(false)
        }
      } else {
        // Reset validation states for incomplete numbers
        setPhoneExistsInSystem(false)
        setPhoneValidationMessage('')
        setIsCheckingPhone(false)
      }
    }
  }

  return (
    <div className="content py-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 col-lg-6 offset-lg-3">
            <div className="box-login text-center bgi-2 bdr-10 c-white bx-sd p-2 mb-3">
              <div className="bgc-16 bdr-10 p-3 p-lg-4">
                <div className="box-regis w-100">
                  <div className="img-regis mx-auto">
                    <img 
                      src={getAssetUrl('/img/logo-m.png')} 
                      alt={branding.siteName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getAssetUrl('/img/logo-m.png')
                      }}
                    />
                  </div>
                </div>

                <h3 className="txt-user mb-1 text-center t-medium">สมัครสมาชิก</h3>

                <div className="wrap justify-content-center mb-3">
                  <small className="bgg-2 bdr-10 c-dark px-2 t-regular me-1">
                    STEP 1
                  </small>
                  <h6 className="text-center">ระบุเบอร์โทร</h6>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-inp bdr-10 position-relative">
                    <div className="icon ps-1">
                      <i className="fas fa-phone-alt"></i>
                    </div>
                    <input
                      type="text"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      placeholder="หมายเลขโทรศัพท์มือถือ"
                      maxLength={10}
                      disabled={isSubmitting || isCheckingPhone}
                      required
                    />
                    {/* Phone checking spinner */}
                    {isCheckingPhone && (
                      <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Checking...</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Phone validation message */}
                  {phoneValidationMessage && (
                    <div className="mt-2 mb-2">
                      <small className={`d-block ${
                        phoneExistsInSystem ? 'text-danger' : 
                        isCheckingPhone ? 'text-warning' : 
                        phoneValidationMessage.includes('✅') ? 'text-success' : 'text-danger'
                      }`}>
                        {phoneValidationMessage}
                      </small>
                    </div>
                  )}

                  {/* SMS Configuration Status with Smart reCAPTCHA Info */}
                  {smsConfigLoading ? (
                    <small className="text-warning">กำลังตรวจสอบการตั้งค่า SMS...</small>
                  ) : smsConfig?.smsEnabled ? (
                    <div>
                      <small className="text-info">📱 เราจะส่ง SMS เพื่อยืนยันหมายเลขโทรศัพท์</small>
                      
                      {/* reCAPTCHA is now invisible - no UI display needed */}
                    </div>
                  ) : smsConfig ? (
                    <small className="text-success">
                      ✅ ไม่ต้องยืนยัน SMS สำหรับการสมัครสมาชิก
                    </small>
                  ) : (
                    <small className="text-danger">
                      ⚠️ ไม่สามารถโหลดการตั้งค่าระบบได้
                    </small>
                  )}

                  {/* reCAPTCHA Container — render whenever SMS enabled so Firebase path (incl. primaryProvider unknown) has a container */}
                  {!smsConfigLoading && smsConfig?.smsEnabled && (
                    <div 
                      id="register-recaptcha-container" 
                      style={{
                        position: 'absolute',
                        left: '-9999px',
                        top: 0,
                        visibility: 'hidden',
                        pointerEvents: 'none'
                      }}
                    />
                  )}

                  <div className="row mx--1 mt-2">
                    <div className="col px-1">
                      <Link
                        to="/login"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10"
                        style={{ textDecoration: 'none' }}
                      >
                        <i className="fas fa-chevron-left me-2"></i> มีไอดีแล้ว
                      </Link>
                    </div>
                    <div className="col px-1">
                      <button
                        type="submit"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10 w-100"
                        disabled={isSubmitting || isCheckingPhone || phoneExistsInSystem || (cooldownUntil != null && Date.now() < cooldownUntil)}
                        style={{ border: 'none' }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            ส่ง SMS...
                          </>
                        ) : isCheckingPhone ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Checking...</span>
                            </div>
                            ตรวจสอบเบอร์...
                          </>
                        ) : cooldownUntil != null && Date.now() < cooldownUntil ? (
                          'ขอ OTP ใหม่'
                        ) : phoneExistsInSystem ? (
                          'เบอร์นี้มีในระบบแล้ว'
                        ) : (
                          'ถัดไป →'
                        )}
                      </button>
                    </div>
                  </div>
                  {cooldownUntil != null && Date.now() < cooldownUntil && (
                    <div className="mt-3 text-center">
                      <small className="d-block text-warning mb-2">
                        {(() => {
                          const remainingMs = Math.max(0, cooldownUntil - Date.now())
                          const min = Math.floor(remainingMs / 60000)
                          const sec = Math.floor((remainingMs % 60000) / 1000)
                          return min > 0
                            ? `กรุณารออีก ${min} นาที ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                            : `กรุณารออีก ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                        })()}
                      </small>
                      {hasRecoverableOtpState ? (
                        <button
                          type="button"
                          className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10 w-100"
                          style={{ border: 'none' }}
                          onClick={navigateToPersistedOtpPage}
                        >
                          ไปยืนยัน OTP
                        </button>
                      ) : null}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
